<template>
  <f7-link
    class="image-dropzone"
    :class="[`image-dropzone--${shape}`, { dragging }]"
    @click="openPicker"
    @dragover.prevent="dragging = true"
    @dragleave="dragging = false"
    @drop.prevent="onDrop"
    role="button"
    tabindex="0"
    @keydown.enter="openPicker"
    @keydown.space.prevent="openPicker"
  >
    <input
      ref="inputRef"
      type="file"
      accept="image/*"
      class="image-dropzone__input"
      @change="onChange"
    />

    <img
      v-if="previewSrc"
      :src="previewSrc"
      class="image-dropzone__preview"
      :alt="label"
    />
    <div v-else class="image-dropzone__empty">
      <span class="material-symbols-outlined" style="font-size: 22px">photo_badge_plus</span>
      <span class="image-dropzone__label">{{ label }}</span>
      <span v-if="hint" class="image-dropzone__hint">{{ hint }}</span>
    </div>

    <div v-if="previewSrc" class="image-dropzone__overlay">
      <f7-icon f7="pencil" :size="16" />
      <span>{{ hasFile ? "Nouvelle image" : "Modifier" }}</span>
    </div>

    <f7-link
      v-if="previewSrc"
      type="button"
      class="image-dropzone__remove"
      @click.stop="remove"
    >
      <f7-icon f7="xmark" :size="12" />
    </f7-link>
  </f7-link>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from "vue";

const props = defineProps({
  label: { type: String, required: true },
  hint: { type: String, default: "" },
  modelValue: { type: String, default: "" },
  shape: { type: String, default: "square" },
});

const emit = defineEmits(["update:modelValue", "select", "clear"]);

const inputRef = ref(null);
const file = ref(null);
const filePreview = ref("");
const dragging = ref(false);

const hasFile = computed(() => !!file.value);
const previewSrc = computed(() => filePreview.value || props.modelValue);

function openPicker() {
  inputRef.value?.click();
}

function onChange(event) {
  const picked = event.target.files?.[0];
  event.target.value = null;
  if (picked) setFile(picked);
}

function onDrop(event) {
  dragging.value = false;
  const picked = event.dataTransfer?.files?.[0];
  if (picked) setFile(picked);
}

function setFile(picked) {
  if (!picked.type.startsWith("image/")) return;
  if (filePreview.value) URL.revokeObjectURL(filePreview.value);
  file.value = picked;
  filePreview.value = URL.createObjectURL(picked);
  emit("select", picked);
  emit("update:modelValue", filePreview.value);
}

function remove() {
  if (filePreview.value) URL.revokeObjectURL(filePreview.value);
  file.value = null;
  filePreview.value = "";
  emit("clear");
  emit("update:modelValue", "");
}

onBeforeUnmount(() => {
  if (filePreview.value) URL.revokeObjectURL(filePreview.value);
});
</script>

<style scoped>
.image-dropzone {
  position: relative;
  overflow: hidden;
  border: 2px dashed var(--f7-list-item-border-color, #d0d0d0);
  border-radius: 12px;
  background: var(--f7-list-bg-color, #f5f5f5);
  transition: border-color 0.2s;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
}
.image-dropzone.dragging {
  border-color: var(--f7-theme-color);
}
.image-dropzone--banner {
  height: 140px;
}
.image-dropzone--square {
  height: 160px;
  width: 160px;
  border-radius: 12px;
}
.image-dropzone--circle {
  height: 100px;
  width: 100px;
  border-radius: 50%;
}
.image-dropzone__input {
  display: none;
}
.image-dropzone__preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.image-dropzone__empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-align: center;
  padding: 8px;
}
.image-dropzone__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.image-dropzone__hint {
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.image-dropzone__overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
}
.image-dropzone__remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
}
.image-dropzone--circle .image-dropzone__remove {
  top: 4px;
  right: 4px;
}
</style>
